import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "../styles/error-status-page.css";

const ERROR_CONTENT = {
  400: {
    title: "Bad request",
    subtitle: "The request could not be processed.",
    description:
      "Some request data was invalid or incomplete. Please go back and try again.",
  },
  401: {
    title: "Authentication required",
    subtitle: "Your session is missing or has expired.",
    description:
      "Please sign in again to continue using the system.",
  },
  403: {
    title: "Access forbidden",
    subtitle: "You do not have permission to access this resource.",
    description:
      "If you believe this is incorrect, contact an administrator.",
  },
  404: {
    title: "Page not found",
    subtitle: "The requested page or resource was not found.",
    description:
      "The link may be outdated or the resource may have been moved.",
  },
  500: {
    title: "Server error",
    subtitle: "Something went wrong on the server.",
    description:
      "Please try again in a moment. If the problem continues, contact support.",
  },
  502: {
    title: "Bad gateway",
    subtitle: "The server received an invalid upstream response.",
    description:
      "This is usually temporary. Please retry shortly.",
  },
  503: {
    title: "Service unavailable",
    subtitle: "The service is temporarily unavailable.",
    description:
      "The system may be under maintenance or temporarily overloaded.",
  },
};

function ErrorStatusPage({ isAuthenticated = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useParams();

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(isAuthenticated ? "/" : "/login", { replace: true });
  };

  const statusCode = Number(status);
  const content = useMemo(() => {
    return (
      ERROR_CONTENT[statusCode] || {
        title: "Unexpected error",
        subtitle: "An unexpected error occurred.",
        description: "Please try again or go back to the previous page.",
      }
    );
  }, [statusCode]);

  const stateMessage =
    typeof location.state?.message === "string" ? location.state.message : "";

  return (
    <div className="error-status-page">
      <div className="error-status-card">
        <div className="error-status-code">{Number.isFinite(statusCode) ? statusCode : "Error"}</div>
        <h1 className="error-status-title">{content.title}</h1>
        <p className="error-status-subtitle">{content.subtitle}</p>
        <p className="error-status-description">{content.description}</p>

        {stateMessage && (
          <div className="error-status-message">
            <strong>Details:</strong> {stateMessage}
          </div>
        )}

        <div className="error-status-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>

          {statusCode === 401 ? (
            <button
              type="button"
              className="primary-btn"
              onClick={() => navigate("/login", { replace: true })}
            >
              Go to Login
            </button>
          ) : (
            <button
              type="button"
              className="primary-btn"
              onClick={handleGoBack}
            >
              Go Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorStatusPage;
